/**
 * Bouton flottant feedback — bug ou suggestion
 * Accessible depuis toutes les pages une fois connecté
 */

import { useState, useEffect, type ReactNode } from 'react';
import { MessageSquare, X, Bug, Lightbulb, Send, ChevronLeft } from 'lucide-react';
import { feedbackApi } from '@/services/api';
import { Button, Input, Label } from '@/components/ui';

type Step = 'closed' | 'type' | 'bug' | 'suggestion' | 'done';

export function FeedbackButton(): ReactNode {
    const [step, setStep] = useState<Step>('closed');
    const [loading, setLoading] = useState(false);

    // Bug form fields
    const [subject, setSubject] = useState('');
    const [steps, setSteps] = useState('');
    const [expected, setExpected] = useState('');
    const [actual, setActual] = useState('');

    // Suggestion form field
    const [suggestion, setSuggestion] = useState('');

    // Allow opening the panel from anywhere (e.g. mobile menu) via a window event
    useEffect(() => {
        const handler = () => setStep('type');
        window.addEventListener('feedback:open', handler);
        return () => window.removeEventListener('feedback:open', handler);
    }, []);

    const reset = () => {
        setSubject(''); setSteps(''); setExpected(''); setActual(''); setSuggestion('');
        setStep('closed');
    };

    const submitBug = async () => {
        if (!subject.trim()) return;
        setLoading(true);
        try {
            await feedbackApi.report({
                type: 'bug',
                subject: subject.trim(),
                steps: steps.trim() || undefined,
                expected: expected.trim() || undefined,
                actual: actual.trim() || undefined,
                page: window.location.pathname,
                userAgent: navigator.userAgent,
            });
            setStep('done');
        } finally {
            setLoading(false);
        }
    };

    const submitSuggestion = async () => {
        if (!suggestion.trim()) return;
        setLoading(true);
        try {
            await feedbackApi.report({
                type: 'suggestion',
                subject: suggestion.trim(),
                page: window.location.pathname,
            });
            setStep('done');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex flex-col items-end gap-3">
            {/* Panel */}
            {step !== 'closed' && (
                <div className="w-[calc(100vw-2rem)] sm:w-[calc(100vw-3rem)] max-w-80 max-h-[70vh] bg-white rounded-lg shadow-lg border border-[#E2E8F0] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                        <div className="flex items-center gap-2">
                            {(step === 'bug' || step === 'suggestion') && (
                                <button
                                    onClick={() => setStep('type')}
                                    className="text-[#64748B] hover:text-[#1E293B] mr-1"
                                    aria-label="Retour"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                            )}
                            <span className="text-sm font-semibold text-[#1E293B]">
                                {step === 'type' && 'Envoyer un retour'}
                                {step === 'bug' && 'Signaler un bug'}
                                {step === 'suggestion' && 'Proposer une idée'}
                                {step === 'done' && 'Merci !'}
                            </span>
                        </div>
                        <button onClick={reset} className="text-[#64748B] hover:text-[#1E293B]" aria-label="Fermer">
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-4 overflow-y-auto">
                        {step === 'type' && (
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setStep('bug')}
                                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#E2E8F0] hover:border-[#0F2744] hover:bg-[#EFF6FF] transition-colors"
                                >
                                    <Bug className="h-6 w-6 text-[#EF4444]" />
                                    <span className="text-sm font-medium text-[#1E293B]">Signaler un bug</span>
                                </button>
                                <button
                                    onClick={() => setStep('suggestion')}
                                    className="flex flex-col items-center gap-2 p-4 rounded-lg border border-[#E2E8F0] hover:border-[#0F2744] hover:bg-[#EFF6FF] transition-colors"
                                >
                                    <Lightbulb className="h-6 w-6 text-[#F59E0B]" />
                                    <span className="text-sm font-medium text-[#1E293B]">Proposer une idée</span>
                                </button>
                            </div>
                        )}

                        {step === 'bug' && (
                            <div className="space-y-3">
                                <div className="space-y-1">
                                    <Label htmlFor="fb-subject" required>Titre du problème</Label>
                                    <Input
                                        id="fb-subject"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder="Ex: Facture non sauvegardée"
                                        maxLength={200}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="fb-steps">Étapes pour reproduire</Label>
                                    <textarea
                                        id="fb-steps"
                                        value={steps}
                                        onChange={(e) => setSteps(e.target.value)}
                                        placeholder="1. J'ouvre une facture&#10;2. Je clique sur Enregistrer&#10;3. ..."
                                        rows={3}
                                        maxLength={2000}
                                        className="w-full rounded-md border border-[#E2E8F0] dark:border-[#334155] bg-white dark:bg-[#1E293B] text-[#1E293B] dark:text-[#F1F5F9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2744] resize-none"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="fb-expected">Comportement attendu</Label>
                                    <Input
                                        id="fb-expected"
                                        value={expected}
                                        onChange={(e) => setExpected(e.target.value)}
                                        placeholder="La facture devrait être enregistrée"
                                        maxLength={1000}
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="fb-actual">Comportement observé</Label>
                                    <Input
                                        id="fb-actual"
                                        value={actual}
                                        onChange={(e) => setActual(e.target.value)}
                                        placeholder="Message d'erreur / rien ne se passe"
                                        maxLength={1000}
                                    />
                                </div>
                                <p className="text-xs text-[#94A3B8]">
                                    Page : <code className="font-mono">{window.location.pathname}</code>
                                </p>
                                <Button
                                    onClick={submitBug}
                                    disabled={!subject.trim() || loading}
                                    isLoading={loading}
                                    className="w-full"
                                    size="sm"
                                >
                                    <Send className="h-3.5 w-3.5" />
                                    Envoyer
                                </Button>
                            </div>
                        )}

                        {step === 'suggestion' && (
                            <div className="space-y-3">
                                <div className="space-y-1">
                                    <Label htmlFor="fb-suggestion" required>Votre idée</Label>
                                    <textarea
                                        id="fb-suggestion"
                                        value={suggestion}
                                        onChange={(e) => setSuggestion(e.target.value)}
                                        placeholder="Décrivez la fonctionnalité que vous aimeriez voir..."
                                        rows={5}
                                        maxLength={2000}
                                        className="w-full rounded-md border border-[#E2E8F0] dark:border-[#334155] bg-white dark:bg-[#1E293B] text-[#1E293B] dark:text-[#F1F5F9] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2744] resize-none"
                                    />
                                </div>
                                <Button
                                    onClick={submitSuggestion}
                                    disabled={!suggestion.trim() || loading}
                                    isLoading={loading}
                                    className="w-full"
                                    size="sm"
                                >
                                    <Send className="h-3.5 w-3.5" />
                                    Envoyer
                                </Button>
                            </div>
                        )}

                        {step === 'done' && (
                            <div className="text-center py-2">
                                <p className="text-[#1E293B] font-medium mb-1">Retour envoyé !</p>
                                <p className="text-sm text-[#64748B]">Merci, nous en tiendrons compte.</p>
                                <button
                                    onClick={reset}
                                    className="mt-3 text-sm text-[#1D4ED8] hover:underline"
                                >
                                    Fermer
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Toggle button — desktop only; on mobile, opened via sidebar menu entry */}
            <button
                onClick={() => setStep(step === 'closed' ? 'type' : 'closed')}
                className="hidden sm:flex w-12 h-12 bg-[#0F2744] hover:bg-[#091A2E] text-white rounded-full shadow-lg items-center justify-center transition-all"
                aria-label="Feedback"
                title="Signaler un bug ou proposer une idée"
            >
                {step !== 'closed' ? <X className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
            </button>
        </div>
    );
}
